/**
 * Названия особенностей классов, подклассов и видов по-русски (D11).
 *
 * Отдельный словарь от `terms.ts`, и не по прихоти: там ключ — запись
 * контента (`srd:dwarf`), здесь — особенность внутри записи
 * (`darkvision`, `second_wind`). Смешать их в одном объекте значило бы
 * потерять проверку «ключ словаря существует в контенте»: она у двух
 * словарей разная.
 *
 * Ключ — `key` особенности, без имени записи. Это безопасно и это
 * проверяется тестом: во всём SRD 5.2 один и тот же ключ всегда несёт
 * одно и то же название («ability_score_improvement» у всех двенадцати
 * классов — та же черта). Появится ключ с двумя разными именами —
 * тест упадёт, и тогда придётся уточнять ключ записью.
 *
 * Переведены **названия**, не описания: описания приходят с сервера
 * переводом записи (`translation.ru`) и живут в `content/translations`.
 * Раньше названия оставались английскими — «Дварф» по-русски,
 * а его черта под ним `Darkvision`.
 *
 * Термины взяты из словаря перевода (`content/translations/
 * glossary.json`): «ярость», «скрытая атака», «тёмное зрение»,
 * «компетентность» — одно понятие, один эквивалент во всех местах.
 * Английские названия лежат рядом и не выдумываются: тест сверяет
 * каждое с полем `name` самой записи контента.
 */

import type { Term } from './terms.js'

export const FEATURE_TERMS: Readonly<Record<string, Term>> = {
  ability_score_improvement: {
    ru: 'Повышение характеристик',
    en: 'Ability Score Improvement',
  },
  abjure_foes: { ru: 'Отвержение врагов', en: 'Abjure Foes' },
  acrobatic_movement: { ru: 'Акробатическое передвижение', en: 'Acrobatic Movement' },
  action_surge: { ru: 'Всплеск действий', en: 'Action Surge' },
  additional_fighting_style: {
    ru: 'Дополнительный боевой стиль',
    en: 'Additional Fighting Style',
  },
  adrenaline_rush: { ru: 'Прилив адреналина', en: 'Adrenaline Rush' },
  arcane_apotheosis: { ru: 'Магический апофеоз', en: 'Arcane Apotheosis' },
  arcane_recovery: { ru: 'Магическое восстановление', en: 'Arcane Recovery' },
  archdruid: { ru: 'Архидруид', en: 'Archdruid' },
  aura_expansion: { ru: 'Расширение ауры', en: 'Aura Expansion' },
  aura_of_courage: { ru: 'Аура отваги', en: 'Aura of Courage' },
  aura_of_devotion: { ru: 'Аура преданности', en: 'Aura of Devotion' },
  aura_of_protection: { ru: 'Аура защиты', en: 'Aura of Protection' },
  barbarian_subclass: { ru: 'Подкласс варвара', en: 'Barbarian Subclass' },
  bard_subclass: { ru: 'Подкласс барда', en: 'Bard Subclass' },
  bardic_inspiration: { ru: 'Вдохновение барда', en: 'Bardic Inspiration' },
  beast_spells: { ru: 'Заклинания зверя', en: 'Beast Spells' },
  blessed_healer: { ru: 'Благословенный целитель', en: 'Blessed Healer' },
  blessed_strikes: { ru: 'Благословенные удары', en: 'Blessed Strikes' },
  body_and_mind: { ru: 'Тело и разум', en: 'Body and Mind' },
  bonus_proficiencies: { ru: 'Дополнительные владения', en: 'Bonus Proficiencies' },
  brave: { ru: 'Храбрость', en: 'Brave' },
  brutal_strike: { ru: 'Жестокий удар', en: 'Brutal Strike' },
  channel_divinity: { ru: 'Божественный канал', en: 'Channel Divinity' },
  circle_of_the_land_spells: {
    ru: 'Заклинания круга земли',
    en: 'Circle of the Land Spells',
  },
  cleric_subclass: { ru: 'Подкласс жреца', en: 'Cleric Subclass' },
  contact_patron: { ru: 'Связь с покровителем', en: 'Contact Patron' },
  countercharm: { ru: 'Контрочарование', en: 'Countercharm' },
  cunning_action: { ru: 'Хитрое действие', en: 'Cunning Action' },
  cunning_strike: { ru: 'Хитрый удар', en: 'Cunning Strike' },
  cutting_words: { ru: 'Едкие слова', en: 'Cutting Words' },
  damage_resistance: { ru: 'Сопротивление урону', en: 'Damage Resistance' },
  danger_sense: { ru: 'Чувство опасности', en: 'Danger Sense' },
  dark_ones_blessing: { ru: 'Благословение Тёмного', en: 'Dark One’s Blessing' },
  dark_ones_own_luck: { ru: 'Удача Тёмного', en: 'Dark One’s Own Luck' },
  darkvision: { ru: 'Тёмное зрение', en: 'Darkvision' },
  defensive_tactics: { ru: 'Оборонительная тактика', en: 'Defensive Tactics' },
  deflect_attacks: { ru: 'Отражение атак', en: 'Deflect Attacks' },
  deflect_energy: { ru: 'Отражение энергии', en: 'Deflect Energy' },
  deft_explorer: { ru: 'Ловкий исследователь', en: 'Deft Explorer' },
  devious_strikes: { ru: 'Коварные удары', en: 'Devious Strikes' },
  disciple_of_life: { ru: 'Ученик жизни', en: 'Disciple of Life' },
  disciplined_survivor: {
    ru: 'Дисциплинированный выживший',
    en: 'Disciplined Survivor',
  },
  divine_intervention: { ru: 'Божественное вмешательство', en: 'Divine Intervention' },
  divine_order: { ru: 'Божественный сан', en: 'Divine Order' },
  // «Наследие», а не «предки»: так названы и остальные пять таких
  // черт — великанов, исчадий, эльфийская и гномья линии, — и тем же
  // словом зовёт этот выбор словарь слотов. Одна черта не может
  // называться двумя словами в двух словарях (D59).
  draconic_ancestry: { ru: 'Драконье наследие', en: 'Draconic Ancestry' },
  draconic_flight: { ru: 'Драконий полёт', en: 'Draconic Flight' },
  draconic_resilience: { ru: 'Драконья стойкость', en: 'Draconic Resilience' },
  draconic_spells: { ru: 'Драконьи заклинания', en: 'Draconic Spells' },
  dragon_companion: { ru: 'Драконий спутник', en: 'Dragon Companion' },
  dragon_wings: { ru: 'Драконьи крылья', en: 'Dragon Wings' },
  druid_subclass: { ru: 'Подкласс друида', en: 'Druid Subclass' },
  druidic: { ru: 'Друидизм', en: 'Druidic' },
  dwarven_resilience: { ru: 'Дварфская стойкость', en: 'Dwarven Resilience' },
  dwarven_toughness: { ru: 'Дварфская крепость', en: 'Dwarven Toughness' },
  eldritch_invocations: { ru: 'Мистические воззвания', en: 'Eldritch Invocations' },
  eldritch_master: { ru: 'Мистический мастер', en: 'Eldritch Master' },
  elemental_affinity: { ru: 'Родство со стихией', en: 'Elemental Affinity' },
  elemental_fury: { ru: 'Стихийная ярость', en: 'Elemental Fury' },
  elusive: { ru: 'Неуловимость', en: 'Elusive' },
  elven_lineage: { ru: 'Эльфийская линия', en: 'Elven Lineage' },
  empowered_evocation: { ru: 'Усиленное воплощение', en: 'Empowered Evocation' },
  empowered_strikes: { ru: 'Усиленные удары', en: 'Empowered Strikes' },
  epic_boon: { ru: 'Эпический дар', en: 'Epic Boon' },
  evasion: { ru: 'Уклонение', en: 'Evasion' },
  evocation_savant: { ru: 'Знаток воплощения', en: 'Evocation Savant' },
  // «Компетентность» — по словарю перевода: Expertise там уже назван,
  // и второй эквивалент («экспертиза») развёл бы одно понятие надвое.
  expertise: { ru: 'Компетентность', en: 'Expertise' },
  extra_attack: { ru: 'Дополнительная атака', en: 'Extra Attack' },
  faithful_steed: { ru: 'Верный скакун', en: 'Faithful Steed' },
  fast_hands: { ru: 'Быстрые руки', en: 'Fast Hands' },
  fast_movement: { ru: 'Быстрое передвижение', en: 'Fast Movement' },
  favored_enemy: { ru: 'Заклятый враг', en: 'Favored Enemy' },
  feral_instinct: { ru: 'Звериное чутьё', en: 'Feral Instinct' },
  feral_senses: { ru: 'Звериные чувства', en: 'Feral Senses' },
  fey_ancestry: { ru: 'Наследие фей', en: 'Fey Ancestry' },
  fiend_spells: { ru: 'Заклинания исчадия', en: 'Fiend Spells' },
  fiendish_legacy: { ru: 'Наследие исчадий', en: 'Fiendish Legacy' },
  fiendish_resilience: { ru: 'Стойкость исчадия', en: 'Fiendish Resilience' },
  fighter_subclass: { ru: 'Подкласс воина', en: 'Fighter Subclass' },
  fighting_style: { ru: 'Боевой стиль', en: 'Fighting Style' },
  fleet_step: { ru: 'Стремительный шаг', en: 'Fleet Step' },
  foe_slayer: { ru: 'Убийца врагов', en: 'Foe Slayer' },
  font_of_inspiration: { ru: 'Источник вдохновения', en: 'Font of Inspiration' },
  font_of_magic: { ru: 'Источник магии', en: 'Font of Magic' },
  forest_gnome: { ru: 'Лесной гном', en: 'Forest Gnome' },
  frenzy: { ru: 'Неистовство', en: 'Frenzy' },
  giant_ancestry: { ru: 'Наследие великанов', en: 'Giant Ancestry' },
  gnomish_cunning: { ru: 'Гномья хитрость', en: 'Gnomish Cunning' },
  gnomish_lineage: { ru: 'Гномья линия', en: 'Gnomish Lineage' },
  greater_divine_intervention: {
    ru: 'Великое божественное вмешательство',
    en: 'Greater Divine Intervention',
  },
  halfling_nimbleness: { ru: 'Проворство полурослика', en: 'Halfling Nimbleness' },
  heightened_focus: { ru: 'Обострённая сосредоточенность', en: 'Heightened Focus' },
  heroic_warrior: { ru: 'Героический воин', en: 'Heroic Warrior' },
  holy_nimbus: { ru: 'Священный нимб', en: 'Holy Nimbus' },
  hunters_lore: { ru: 'Знания охотника', en: 'Hunter’s Lore' },
  hunters_prey: { ru: 'Добыча охотника', en: 'Hunter’s Prey' },
  hurl_through_hell: { ru: 'Бросок через ад', en: 'Hurl Through Hell' },
  improved_blessed_strikes: {
    ru: 'Улучшенные благословенные удары',
    en: 'Improved Blessed Strikes',
  },
  improved_brutal_strike: {
    ru: 'Улучшенный жестокий удар',
    en: 'Improved Brutal Strike',
  },
  // Тот же заголовок на 17 уровне варвара: в источнике особенность
  // называется так же, а ключ разведён суффиксом (D40).
  improved_brutal_strike_17: {
    ru: 'Улучшенный жестокий удар',
    en: 'Improved Brutal Strike',
  },
  improved_critical: {
    ru: 'Улучшенное критическое попадание',
    en: 'Improved Critical',
  },
  improved_cunning_strike: {
    ru: 'Улучшенный хитрый удар',
    en: 'Improved Cunning Strike',
  },
  improved_elemental_fury: {
    ru: 'Улучшенная стихийная ярость',
    en: 'Improved Elemental Fury',
  },
  indomitable: { ru: 'Несгибаемость', en: 'Indomitable' },
  indomitable_might: { ru: 'Несгибаемая мощь', en: 'Indomitable Might' },
  innate_sorcery: { ru: 'Врождённое чародейство', en: 'Innate Sorcery' },
  instinctive_pounce: { ru: 'Инстинктивный прыжок', en: 'Instinctive Pounce' },
  intimidating_presence: { ru: 'Устрашающее присутствие', en: 'Intimidating Presence' },
  jack_of_all_trades: { ru: 'Мастер на все руки', en: 'Jack of All Trades' },
  keen_senses: { ru: 'Острые чувства', en: 'Keen Senses' },
  lands_aid: { ru: 'Помощь земли', en: 'Land’s Aid' },
  large_form: { ru: 'Большая форма', en: 'Large Form' },
  lay_on_hands: { ru: 'Наложение рук', en: 'Lay On Hands' },
  life_domain_spells: { ru: 'Заклинания домена жизни', en: 'Life Domain Spells' },
  luck: { ru: 'Удача', en: 'Luck' },
  magical_cunning: { ru: 'Магическая хитрость', en: 'Magical Cunning' },
  magical_discoveries: { ru: 'Магические открытия', en: 'Magical Discoveries' },
  magical_secrets: { ru: 'Магические тайны', en: 'Magical Secrets' },
  martial_arts: { ru: 'Боевые искусства', en: 'Martial Arts' },
  memorize_spell: { ru: 'Запоминание заклинания', en: 'Memorize Spell' },
  metamagic: { ru: 'Метамагия', en: 'Metamagic' },
  mindless_rage: { ru: 'Бездумная ярость', en: 'Mindless Rage' },
  monk_subclass: { ru: 'Подкласс монаха', en: 'Monk Subclass' },
  monks_focus: { ru: 'Фокус монаха', en: 'Monk’s Focus' },
  mystic_arcanum: { ru: 'Мистический арканум', en: 'Mystic Arcanum' },
  natural_recovery: { ru: 'Природное восстановление', en: 'Natural Recovery' },
  naturally_stealthy: { ru: 'Природная скрытность', en: 'Naturally Stealthy' },
  natures_sanctuary: { ru: 'Убежище природы', en: 'Nature’s Sanctuary' },
  natures_veil: { ru: 'Покров природы', en: 'Nature’s Veil' },
  natures_ward: { ru: 'Защита природы', en: 'Nature’s Ward' },
  oath_of_devotion_spells: {
    ru: 'Заклинания клятвы преданности',
    en: 'Oath of Devotion Spells',
  },
  open_hand_technique: { ru: 'Техника открытой ладони', en: 'Open Hand Technique' },
  otherworldly_presence: {
    ru: 'Потустороннее присутствие',
    en: 'Otherworldly Presence',
  },
  overchannel: { ru: 'Сверхканалирование', en: 'Overchannel' },
  pact_magic: { ru: 'Магия договора', en: 'Pact Magic' },
  paladin_subclass: { ru: 'Подкласс паладина', en: 'Paladin Subclass' },
  paladins_smite: { ru: 'Кара паладина', en: 'Paladin’s Smite' },
  peerless_skill: { ru: 'Непревзойдённое мастерство', en: 'Peerless Skill' },
  perfect_focus: { ru: 'Совершенный фокус', en: 'Perfect Focus' },
  persistent_rage: { ru: 'Неутихающая ярость', en: 'Persistent Rage' },
  potent_cantrip: { ru: 'Мощный заговор', en: 'Potent Cantrip' },
  powerful_build: { ru: 'Мощное телосложение', en: 'Powerful Build' },
  precise_hunter: { ru: 'Точный охотник', en: 'Precise Hunter' },
  preserve_life: { ru: 'Сохранение жизни', en: 'Preserve Life' },
  primal_champion: { ru: 'Первобытный чемпион', en: 'Primal Champion' },
  primal_knowledge: { ru: 'Первобытные знания', en: 'Primal Knowledge' },
  primal_order: { ru: 'Первобытный сан', en: 'Primal Order' },
  quivering_palm: { ru: 'Трепещущая ладонь', en: 'Quivering Palm' },
  radiant_strikes: { ru: 'Лучезарные удары', en: 'Radiant Strikes' },
  rage: { ru: 'Ярость', en: 'Rage' },
  ranger_subclass: { ru: 'Подкласс следопыта', en: 'Ranger Subclass' },
  reckless_attack: { ru: 'Безрассудная атака', en: 'Reckless Attack' },
  relentless_endurance: { ru: 'Неумолимая выносливость', en: 'Relentless Endurance' },
  relentless_hunter: { ru: 'Неумолимый охотник', en: 'Relentless Hunter' },
  relentless_rage: { ru: 'Неумолимая ярость', en: 'Relentless Rage' },
  reliable_talent: { ru: 'Надёжный талант', en: 'Reliable Talent' },
  remarkable_athlete: { ru: 'Выдающийся атлет', en: 'Remarkable Athlete' },
  resourceful: { ru: 'Находчивость', en: 'Resourceful' },
  restoring_touch: { ru: 'Восстанавливающее прикосновение', en: 'Restoring Touch' },
  retaliation: { ru: 'Возмездие', en: 'Retaliation' },
  ritual_adept: { ru: 'Знаток ритуалов', en: 'Ritual Adept' },
  rock_gnome: { ru: 'Скальный гном', en: 'Rock Gnome' },
  rogue_subclass: { ru: 'Подкласс плута', en: 'Rogue Subclass' },
  roving: { ru: 'Странствие', en: 'Roving' },
  sacred_weapon: { ru: 'Священное оружие', en: 'Sacred Weapon' },
  scholar: { ru: 'Учёный', en: 'Scholar' },
  sculpt_spells: { ru: 'Ваяние заклинаний', en: 'Sculpt Spells' },
  sear_undead: { ru: 'Опаление нежити', en: 'Sear Undead' },
  second_story_work: { ru: 'Работа на высоте', en: 'Second-Story Work' },
  second_wind: { ru: 'Второе дыхание', en: 'Second Wind' },
  self_restoration: { ru: 'Самовосстановление', en: 'Self-Restoration' },
  signature_spells: { ru: 'Коронные заклинания', en: 'Signature Spells' },
  skillful: { ru: 'Умелость', en: 'Skillful' },
  slippery_mind: { ru: 'Скользкий разум', en: 'Slippery Mind' },
  slow_fall: { ru: 'Замедленное падение', en: 'Slow Fall' },
  smite_of_protection: { ru: 'Кара защиты', en: 'Smite of Protection' },
  sneak_attack: { ru: 'Скрытая атака', en: 'Sneak Attack' },
  sorcerer_subclass: { ru: 'Подкласс чародея', en: 'Sorcerer Subclass' },
  sorcerous_restoration: {
    ru: 'Чародейское восстановление',
    en: 'Sorcerous Restoration',
  },
  sorcery_incarnate: { ru: 'Воплощённое чародейство', en: 'Sorcery Incarnate' },
  spell_mastery: { ru: 'Мастерство заклинаний', en: 'Spell Mastery' },
  spellcasting: { ru: 'Использование заклинаний', en: 'Spellcasting' },
  steady_aim: { ru: 'Точный прицел', en: 'Steady Aim' },
  stonecunning: { ru: 'Знание камня', en: 'Stonecunning' },
  stroke_of_luck: { ru: 'Удачный случай', en: 'Stroke of Luck' },
  studied_attacks: { ru: 'Изученные атаки', en: 'Studied Attacks' },
  stunning_strike: { ru: 'Ошеломляющий удар', en: 'Stunning Strike' },
  superior_critical: {
    ru: 'Превосходное критическое попадание',
    en: 'Superior Critical',
  },
  superior_defense: { ru: 'Превосходная защита', en: 'Superior Defense' },
  superior_hunters_defense: {
    ru: 'Превосходная защита охотника',
    en: 'Superior Hunter’s Defense',
  },
  superior_hunters_prey: {
    ru: 'Превосходная добыча охотника',
    en: 'Superior Hunter’s Prey',
  },
  superior_inspiration: { ru: 'Превосходное вдохновение', en: 'Superior Inspiration' },
  supreme_healing: { ru: 'Высшее исцеление', en: 'Supreme Healing' },
  supreme_sneak: { ru: 'Высшая скрытность', en: 'Supreme Sneak' },
  survivor: { ru: 'Выживший', en: 'Survivor' },
  tactical_master: { ru: 'Тактический мастер', en: 'Tactical Master' },
  tactical_mind: { ru: 'Тактический разум', en: 'Tactical Mind' },
  tactical_shift: { ru: 'Тактическое смещение', en: 'Tactical Shift' },
  thiefs_reflexes: { ru: 'Рефлексы вора', en: 'Thief’s Reflexes' },
  thieves_cant: { ru: 'Воровской жаргон', en: 'Thieves’ Cant' },
  three_extra_attacks: { ru: 'Три дополнительные атаки', en: 'Three Extra Attacks' },
  tireless: { ru: 'Неутомимость', en: 'Tireless' },
  trance: { ru: 'Транс', en: 'Trance' },
  two_extra_attacks: { ru: 'Две дополнительные атаки', en: 'Two Extra Attacks' },
  unarmored_defense: { ru: 'Защита без доспехов', en: 'Unarmored Defense' },
  unarmored_movement: { ru: 'Передвижение без доспехов', en: 'Unarmored Movement' },
  uncanny_dodge: { ru: 'Сверхъестественное уклонение', en: 'Uncanny Dodge' },
  uncanny_metabolism: { ru: 'Сверхъестественный метаболизм', en: 'Uncanny Metabolism' },
  use_magic_device: {
    ru: 'Использование магических предметов',
    en: 'Use Magic Device',
  },
  // «Разносторонность», как в словаре слотов: это одна и та же черта
  // человека, и видел её тестер именно под этим именем (D59).
  versatile: { ru: 'Разносторонность', en: 'Versatile' },
  warlock_subclass: { ru: 'Подкласс колдуна', en: 'Warlock Subclass' },
  weapon_mastery: { ru: 'Мастерство оружия', en: 'Weapon Mastery' },
  wholeness_of_body: { ru: 'Целостность тела', en: 'Wholeness of Body' },
  wild_companion: { ru: 'Дикий спутник', en: 'Wild Companion' },
  wild_resurgence: { ru: 'Дикое возрождение', en: 'Wild Resurgence' },
  wild_shape: { ru: 'Дикий облик', en: 'Wild Shape' },
  wizard_subclass: { ru: 'Подкласс волшебника', en: 'Wizard Subclass' },
  words_of_creation: { ru: 'Слова созидания', en: 'Words of Creation' },
  // ── Атаки монстров (D50) ──
  //
  // Только атаки: они выходят кнопкой в очередь боя, и английское
  // название там читается так же плохо, как непереведённая черта
  // на листе. Названия прочих черт и действий монстра на экран пока
  // не выходят — выйдут, придут сюда же.
  abyssal_glaive: { ru: 'Бездонная глефа', en: 'Abyssal Glaive' },
  arcane_burst: { ru: 'Магический всплеск', en: 'Arcane Burst' },
  attach: { ru: 'Присасывание', en: 'Attach' },
  battleaxe: { ru: 'Боевой топор', en: 'Battleaxe' },
  beak: { ru: 'Клюв', en: 'Beak' },
  beaks: { ru: 'Клювы', en: 'Beaks' },
  beard: { ru: 'Борода', en: 'Beard' },
  bite: { ru: 'Укус', en: 'Bite' },
  bites: { ru: 'Укусы', en: 'Bites' },
  bladed_arm: { ru: 'Клинковая рука', en: 'Bladed Arm' },
  bone_bow: { ru: 'Костяной лук', en: 'Bone Bow' },
  bone_cudgel: { ru: 'Костяная дубина', en: 'Bone Cudgel' },
  boulder: { ru: 'Валун', en: 'Boulder' },
  burn: { ru: 'Ожог', en: 'Burn' },
  burning_hammer: { ru: 'Пылающий молот', en: 'Burning Hammer' },
  chain: { ru: 'Цепь', en: 'Chain' },
  channel_negative_energy: {
    ru: 'Канал негативной энергии',
    en: 'Channel Negative Energy',
  },
  charged_tendril: { ru: 'Заряженный усик', en: 'Charged Tendril' },
  claw: { ru: 'Коготь', en: 'Claw' },
  claws: { ru: 'Когти', en: 'Claws' },
  club: { ru: 'Дубинка', en: 'Club' },
  crush: { ru: 'Сдавливание', en: 'Crush' },
  cursed_touch: { ru: 'Проклятое касание', en: 'Cursed Touch' },
  dagger: { ru: 'Кинжал', en: 'Dagger' },
  devilish_claw: { ru: 'Дьявольский коготь', en: 'Devilish Claw' },
  dissolving_pseudopod: { ru: 'Разъедающая псевдоподия', en: 'Dissolving Pseudopod' },
  draining_swipe: { ru: 'Иссушающий взмах', en: 'Draining Swipe' },
  eldritch_burst: { ru: 'Колдовской всплеск', en: 'Eldritch Burst' },
  enchanting_bow: { ru: 'Чарующий лук', en: 'Enchanting Bow' },
  fiendish_touch: { ru: 'Дьявольское касание', en: 'Fiendish Touch' },
  fiery_bolt: { ru: 'Огненный разряд', en: 'Fiery Bolt' },
  fiery_mace: { ru: 'Огненная булава', en: 'Fiery Mace' },
  fist: { ru: 'Кулак', en: 'Fist' },
  flame_spear: { ru: 'Пламенное копьё', en: 'Flame Spear' },
  flame_sword: { ru: 'Огненный меч', en: 'Flame Sword' },
  flame_whip: { ru: 'Огненный хлыст', en: 'Flame Whip' },
  flying_sword: { ru: 'Летающий меч', en: 'Flying Sword' },
  force_bolt: { ru: 'Силовой разряд', en: 'Force Bolt' },
  foreleg: { ru: 'Передняя лапа', en: 'Foreleg' },
  frost_axe: { ru: 'Морозный топор', en: 'Frost Axe' },
  gore: { ru: 'Рога', en: 'Gore' },
  gouge: { ru: 'Вспарывание', en: 'Gouge' },
  grab: { ru: 'Хватка', en: 'Grab' },
  grave_strike: { ru: 'Могильный удар', en: 'Grave Strike' },
  great_bow: { ru: 'Большой лук', en: 'Great Bow' },
  greataxe: { ru: 'Секира', en: 'Greataxe' },
  greatclub: { ru: 'Палица', en: 'Greatclub' },
  greatsword: { ru: 'Двуручный меч', en: 'Greatsword' },
  hail_of_bark: { ru: 'Град коры', en: 'Hail of Bark' },
  hammer_throw: { ru: 'Бросок молота', en: 'Hammer Throw' },
  hand_crossbow: { ru: 'Одноручный арбалет', en: 'Hand Crossbow' },
  handaxe: { ru: 'Ручной топор', en: 'Handaxe' },
  harpoon: { ru: 'Гарпун', en: 'Harpoon' },
  heated_blade: { ru: 'Раскалённый клинок', en: 'Heated Blade' },
  heavy_crossbow: { ru: 'Тяжёлый арбалет', en: 'Heavy Crossbow' },
  holy_mace: { ru: 'Священная булава', en: 'Holy Mace' },
  hooves: { ru: 'Копыта', en: 'Hooves' },
  hurl_flame: { ru: 'Метание пламени', en: 'Hurl Flame' },
  ice_spear: { ru: 'Ледяное копьё', en: 'Ice Spear' },
  infernal_glaive: { ru: 'Инфернальная глефа', en: 'Infernal Glaive' },
  infernal_sting: { ru: 'Инфернальное жало', en: 'Infernal Sting' },
  javelin: { ru: 'Метательное копьё', en: 'Javelin' },
  life_drain: { ru: 'Вытягивание жизни', en: 'Life Drain' },
  light_crossbow: { ru: 'Лёгкий арбалет', en: 'Light Crossbow' },
  light_hammer: { ru: 'Лёгкий молот', en: 'Light Hammer' },
  lightning_blade: { ru: 'Клинок молний', en: 'Lightning Blade' },
  longbow: { ru: 'Длинный лук', en: 'Longbow' },
  longsword: { ru: 'Длинный меч', en: 'Longsword' },
  mace: { ru: 'Булава', en: 'Mace' },
  morningstar: { ru: 'Моргенштерн', en: 'Morningstar' },
  necrotic_bow: { ru: 'Некротический лук', en: 'Necrotic Bow' },
  necrotic_ray: { ru: 'Некротический луч', en: 'Necrotic Ray' },
  necrotic_sword: { ru: 'Некротический меч', en: 'Necrotic Sword' },
  needle_sword: { ru: 'Меч-игла', en: 'Needle Sword' },
  nightmare_ray: { ru: 'Кошмарный луч', en: 'Nightmare Ray' },
  ocean_spear: { ru: 'Океанское копьё', en: 'Ocean Spear' },
  pact_blade: { ru: 'Клинок договора', en: 'Pact Blade' },
  paralyzing_touch: { ru: 'Парализующее касание', en: 'Paralyzing Touch' },
  petrifying_bite: { ru: 'Окаменяющий укус', en: 'Petrifying Bite' },
  pike: { ru: 'Пика', en: 'Pike' },
  pincer: { ru: 'Клешня', en: 'Pincer' },
  pistol: { ru: 'Пистоль', en: 'Pistol' },
  poison_burst: { ru: 'Ядовитый выброс', en: 'Poison Burst' },
  poison_ray: { ru: 'Ядовитый луч', en: 'Poison Ray' },
  proboscis: { ru: 'Хоботок', en: 'Proboscis' },
  pseudopod: { ru: 'Псевдоподия', en: 'Pseudopod' },
  radiant_flame: { ru: 'Лучезарное пламя', en: 'Radiant Flame' },
  radiant_horn: { ru: 'Лучезарный рог', en: 'Radiant Horn' },
  radiant_sword: { ru: 'Лучезарный меч', en: 'Radiant Sword' },
  rake: { ru: 'Царапина', en: 'Rake' },
  ram: { ru: 'Таран', en: 'Ram' },
  rapier: { ru: 'Рапира', en: 'Rapier' },
  rend: { ru: 'Рвание', en: 'Rend' },
  restless_touch: { ru: 'Беспокойное касание', en: 'Restless Touch' },
  ritual_sickle: { ru: 'Ритуальный серп', en: 'Ritual Sickle' },
  rock: { ru: 'Камень', en: 'Rock' },
  rock_launch: { ru: 'Бросок камня', en: 'Rock Launch' },
  rotting_fist: { ru: 'Гниющий кулак', en: 'Rotting Fist' },
  rotting_touch: { ru: 'Гнилостное касание', en: 'Rotting Touch' },
  scimitar: { ru: 'Скимитар', en: 'Scimitar' },
  scratch: { ru: 'Царапанье', en: 'Scratch' },
  searing_fork: { ru: 'Раскалённые вилы', en: 'Searing Fork' },
  shock: { ru: 'Разряд', en: 'Shock' },
  shortbow: { ru: 'Короткий лук', en: 'Shortbow' },
  shortsword: { ru: 'Короткий меч', en: 'Shortsword' },
  shred: { ru: 'Раздирание', en: 'Shred' },
  slam: { ru: 'Удар', en: 'Slam' },
  slash: { ru: 'Рассечение', en: 'Slash' },
  smother: { ru: 'Удушение', en: 'Smother' },
  snake_hair: { ru: 'Змеи-волосы', en: 'Snake Hair' },
  spear: { ru: 'Копьё', en: 'Spear' },
  sting: { ru: 'Жало', en: 'Sting' },
  stone_club: { ru: 'Каменная дубина', en: 'Stone Club' },
  storm_blade: { ru: 'Грозовой клинок', en: 'Storm Blade' },
  storm_bolt: { ru: 'Грозовой разряд', en: 'Storm Bolt' },
  storm_sword: { ru: 'Грозовой меч', en: 'Storm Sword' },
  swarm_of_grasping_hands: { ru: 'Рой хватающих рук', en: 'Swarm of Grasping Hands' },
  tail: { ru: 'Хвост', en: 'Tail' },
  tail_spike: { ru: 'Хвостовой шип', en: 'Tail Spike' },
  tail_stinger: { ru: 'Хвостовое жало', en: 'Tail Stinger' },
  talons: { ru: 'Когти птицы', en: 'Talons' },
  tentacle: { ru: 'Щупальце', en: 'Tentacle' },
  tentacles: { ru: 'Щупальца', en: 'Tentacles' },
  thorn_burst: { ru: 'Шипастый выброс', en: 'Thorn Burst' },
  thunderbolt: { ru: 'Громовая стрела', en: 'Thunderbolt' },
  thundercloud: { ru: 'Громовое облако', en: 'Thundercloud' },
  thunderous_mace: { ru: 'Громовая булава', en: 'Thunderous Mace' },
  thunderous_slam: { ru: 'Громовой удар', en: 'Thunderous Slam' },
  touch: { ru: 'Касание', en: 'Touch' },
  trash_lob: { ru: 'Бросок мусора', en: 'Trash Lob' },
  tree_club: { ru: 'Дубина-дерево', en: 'Tree Club' },
  tusk: { ru: 'Клык', en: 'Tusk' },
  umbral_dagger: { ru: 'Теневой кинжал', en: 'Umbral Dagger' },
  verdant_wisp: { ru: 'Зелёный огонёк', en: 'Verdant Wisp' },
  vile_slime: { ru: 'Мерзкая слизь', en: 'Vile Slime' },
  vine_lash: { ru: 'Лозовый хлыст', en: 'Vine Lash' },
  vine_staff: { ru: 'Лозовый посох', en: 'Vine Staff' },
  warhammer: { ru: 'Боевой молот', en: 'Warhammer' },
  wind_swipe: { ru: 'Порыв ветра', en: 'Wind Swipe' },
  withering_sword: { ru: 'Иссушающий меч', en: 'Withering Sword' },
  withering_touch: { ru: 'Иссушающее касание', en: 'Withering Touch' },
}

/** Словарь одного языка: то, что уходит в ресурсы i18next. */
export function featureTermsFor(language: 'ru' | 'en'): Record<string, string> {
  return Object.fromEntries(
    Object.entries(FEATURE_TERMS).map(([key, term]) => [key, term[language]]),
  )
}
